import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, apiErr, SESSION_EXPIRED_EVENT } from "@/lib/api";
import { toast } from "sonner";
import i18n from "@/i18n";

const AuthContext = createContext(null);
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_BRAND = { app_name: "NEXTMIND INFISSI", app_subtitle: "Piattaforma Preventivi AI", logoAbs: null, iconAbs: null };

function applyUserLocale(u) {
  if (u && u.locale && i18n.language !== u.locale) {
    i18n.changeLanguage(u.locale);
    try { localStorage.setItem("nm_locale", u.locale); } catch {}
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [error, setError] = useState("");
  const [branding, setBranding] = useState(null);

  const loadPublicBranding = useCallback(async () => {
    try {
      const { data } = await http.get("/branding/public");
      setBranding({ ...data, logoAbs: data.logo_url ? BACKEND + data.logo_url : null, iconAbs: data.icon_url ? BACKEND + data.icon_url : null });
    } catch {
      setBranding(PLATFORM_BRAND);
    }
  }, []);

  const loadTenantBranding = useCallback(async () => {
    try {
      const { data } = await http.get("/branding/me");
      setBranding({ ...data, logoAbs: data.logo_url ? BACKEND + data.logo_url : null, iconAbs: data.icon_url ? BACKEND + data.icon_url : null });
    } catch {
      await loadPublicBranding();
    }
  }, [loadPublicBranding]);

  const refreshBranding = useCallback(async () => {
    // used by settings after branding changes
    await loadTenantBranding();
  }, [loadTenantBranding]);

  useEffect(() => {
    http.get("/auth/me")
      .then((r) => { setUser(r.data); applyUserLocale(r.data); loadTenantBranding(); })
      .catch(() => { setUser(false); loadPublicBranding(); });
  }, [loadTenantBranding, loadPublicBranding]);

  useEffect(() => {
    const onExpired = () => {
      setUser((u) => {
        if (u) toast.error("Sessione scaduta: accedi di nuovo.", { id: "session-expired" });
        return false;
      });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const refreshUser = useCallback(async () => {
    try { const r = await http.get("/auth/me"); setUser(r.data); applyUserLocale(r.data); } catch { setUser(false); }
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      await http.post("/auth/login", { email, password });
      const me = await http.get("/auth/me");
      setUser(me.data);
      applyUserLocale(me.data);
      await loadTenantBranding();
      return true;
    } catch (e) {
      setError(apiErr(e, "Credenziali non valide"));
      return false;
    }
  };

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch {}
    setUser(false);
    loadPublicBranding();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, error, setError, branding, refreshBranding, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
